import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { Observable, from, of } from 'rxjs';
import { filter, take, timeout, catchError, switchMap, map } from 'rxjs/operators';
import { DeviceDetectorService } from 'ngx-device-detector';
import { LocalStorageService } from './connectors/localstorage';
import { UserBehaviorService } from './services/user-events/user-behavior.service';
import { AccountInfo } from '@azure/msal-browser';
import { LogoutService } from './services/auth/logout.service';
import { AuthService } from './services/auth/authService.service';
import { MenuBootstrapService } from './home/core/services/menu-bootstrap.service';
import { MsalBootstrapService } from './services/auth/msal-bootstrap.service';
import { PageAccessService } from './services/auth/page-access.service';
import { resolvePagePath } from '@core/routing/resolve-page-path';

@Injectable({
  providedIn: 'root'
})
export class MicrosoftLoginGuard implements CanActivate {
  deviceInfo: any;

  constructor(
    private authService: MsalService,
    private userBehavior: UserBehaviorService,
    private router: Router,
    private deviceService: DeviceDetectorService,
    private localStorage: LocalStorageService,
    private logoutService: LogoutService,
    private appAuthService: AuthService,
    private menuBootstrapService: MenuBootstrapService,
    private msalBootstrapService: MsalBootstrapService,
    private pageAccessService: PageAccessService
  ) {
    this.epicFunction();
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    return from(
      this.msalBootstrapService.ensureReady(this.authService, this.appAuthService)
    ).pipe(
      switchMap(() => {
        const loggedByMsal = this.authService.instance.getActiveAccount();

        if (loggedByMsal == null) {
          this.reset();
          this.router.navigate(['/login']);
          return of(false);
        }

        this.setMsalLoggedInUser(loggedByMsal);

        const isLandingPage = state.url.startsWith('/home') || state.url === '/' || state.url.startsWith('/login');
        const returnUrl = this.logoutService.getReturnUrl();

        if (returnUrl && isLandingPage && returnUrl !== state.url && returnUrl !== '/login') {
          this.logoutService.consumeReturnUrl();
          this.router.navigateByUrl(returnUrl);
          return of(false);
        }

        return this.appAuthService.loginCompleted$.pipe(
          filter((completed) => completed),
          take(1),
          timeout(8000),
          switchMap(() => this.#checkRoutePermission(route, state)),
          catchError(() => this.#checkRoutePermission(route, state))
        );
      })
    );
  }

  #checkRoutePermission(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    const pagePath = resolvePagePath(route);

    if (!pagePath) {
      return of(true);
    }

    return this.pageAccessService.check(pagePath).pipe(
      map((allowed) => {
        if (!allowed) {
          this.router.navigate(['/access-denied']);
        }

        return allowed;
      }),
      catchError(() => of(false))
    );
  }

  reset(): void {
    this.userBehavior.setUser({
      name: '',
      email: '',
      from: ''
    });

    this.userBehavior.resetLocalStorageEvent();

    this.localStorage.remove('profile');
    this.localStorage.remove('userId');

    this.menuBootstrapService.reset();
  }

  setMsalLoggedInUser(user: AccountInfo): void {
    if (!user) {
      return;
    }

    this.userBehavior.setUser({
      name: user.name,
      email: user.username,
      from: 'msal'
    });
  }

  epicFunction() {
    this.deviceInfo = this.deviceService.getDeviceInfo();
  }
}
